import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const useWindowWidth = () => {
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  return windowWidth;
};

const useCounterAnimation = () => {
  const [counts, setCounts] = useState({
    years: 0,
    countries: 0,
    products: 0,
    certificates: 0
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setCounts(prevCounts => ({
        years: Math.min(prevCounts.years + 1, 24),
        countries: Math.min(prevCounts.countries + 1, 100),
        products: Math.min(prevCounts.products + 1, 10),
        certificates: Math.min(prevCounts.certificates + 1, 10)
      }));
    }, 50);

    return () => clearInterval(interval);
  }, []);

  return counts;
};

const COMMON_STYLES = {
  oswald: {
    fontFamily: 'Oswald, sans-serif'
  },
  statBox: {
    color: 'white',
    marginTop: '10px',
    marginBottom: '20px',
    width: '100%',
    maxWidth: '400px'
  },
  statTitle: {
    fontSize: 'clamp(18px, 4vw, 24px)',
    fontWeight: 'bold',
    marginBottom: '5px',
    marginRight: '18px',
    fontFamily: 'Oswald, sans-serif'
  },
  statDescription: {
    fontSize: 'clamp(12px, 3vw, 14px)',
    opacity: '0.9',
    fontWeight: '200',
    marginRight: '18px',
    fontFamily: 'Inter, sans-serif',
    color: 'white'
  }
};

const WordAnimation = memo(({ children, delay, isAnimating }) => (
  <div style={{
    overflow: 'hidden',
    display: 'inline-block',
  }}>
    <div style={{
      transform: isAnimating ? 'translateY(0)' : 'translateY(100%)',
      opacity: isAnimating ? 1 : 0,
      transition: `transform 0.8s ease ${delay}s, opacity 0.8s ease ${delay}s`,
    }}>
      {children}
    </div>
  </div>
));

const Stats = memo(({ isMobile, currentSlide, isAnimating, counts }) => {
  const statItems = [
    {
      value: `${counts.years} /7 SUPPORT`,
      description: 'Our dedicated 24/7 support keeps your systems running smoothly.'
    },
    {
      value: `${counts.countries}% COST OPTIMIZATION`,
      description: 'We drive infrastructure optimization for sustained cost savings.'
    },
    {
      value: `${counts.products}+ PRODUCTS`,
      description: 'Providing scalable solutions across 200+ business cases.'
    },
    {
      value: `${counts.certificates}+ MANAGED SERVICES`,
      description: 'Delivering reliable support for global business needs.'
    }
  ];

  return (
    <div style={{
      flex: isMobile ? '1' : '0 0 40%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: isMobile ? 'center' : 'flex-end',
      padding: '20px',
      width: '100%',
      maxWidth: isMobile ? '100%' : '600px'
    }}>
      {statItems.map((item, index) => (
        <div
          key={index}
          style={{
            ...COMMON_STYLES.statBox,
            textAlign: isMobile ? 'center' : 'right',
            opacity: currentSlide === 0 && isAnimating ? 1 : 0,
            transform: currentSlide === 0 && isAnimating ? 'translateX(0)' : 'translateX(20px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease'
          }}
        >
          <div style={COMMON_STYLES.statTitle}>{item.value}</div>
          <div style={COMMON_STYLES.statDescription}>{item.description}</div>
        </div>
      ))}
    </div>
  );
});


const RepairSlide = () => {
  const containerStyle = {
    position: 'relative',
    width: '100%',
    height: '100vh',
    overflow: 'hidden',
    backgroundImage: 'url("/bg_3.png")',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  const overlayStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  };

  const splitLayoutStyle = {
    display: 'flex',
    flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
    height: '100%',
    maxWidth: '1400px',
    margin: '0 auto',
    padding: window.innerWidth <= 768 ? '20px' : '40px 20px',
  };

  const leftSideStyle = {
    flex: window.innerWidth <= 768 ? 'none' : '1',
    display: window.innerWidth <= 768 ? 'none' : 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    paddingRight: window.innerWidth <= 768 ? '0' : '40px',
    marginTop: window.innerWidth <= 768 ? '20px' : '40px',
    opacity: 0,
    transform: 'translateX(-50px)',
    animation: 'slideIn 0.8s forwards',
  };

  const rightSideStyle = {
    flex: '1',
    paddingLeft: window.innerWidth <= 768 ? '0' : '40px',
    marginTop: window.innerWidth <= 768 ? '180px' : '130px',
  };

  const leftTitleStyle = {
    fontSize: window.innerWidth <= 768 ? '32px' : '54px',
    fontWeight: '600',
    color: 'white',
    lineHeight: window.innerWidth <= 768 ? '48px' : '80px',
    fontFamily: 'Oswald, sans-serif',
    marginLeft: window.innerWidth <= 768 ? '20px' : '70px',
    textAlign: 'left',
    textShadow: '2px 2px 8px rgba(0, 0, 0, 0.6)'
  };

  const headingStyle = {
    color: 'white',
    fontSize: window.innerWidth <= 768 ? '20px' : '24px',
    fontWeight: '700',
    lineHeight: window.innerWidth <= 768 ? '32px' : '38px',
    marginBottom: '10px',
    textAlign: 'left',
  };

  const paragraphStyle = {
    fontSize: window.innerWidth <= 768 ? '13px' : '14px',
    fontFamily: 'Inter, sans-serif',
    lineHeight: '22px',
    fontWeight: '200',
    maxWidth: '500px',
    color: 'white',
    textAlign: 'left',
  };

  const timelineStyle = {
    position: 'relative',
    paddingLeft: window.innerWidth <= 768 ? '15px' : '25px',
    borderLeft: '2px solid #0047AB',
  };

  const sectionStyle = (delay) => ({
    marginBottom: '30px',
    position: 'relative',
    opacity: 0,
    transform: 'translateY(20px)',
    animation: `fadeInUp 0.5s forwards ${delay}s`,
  });

  const circleStyle = {
    position: 'absolute',
    left: '-36px',
    width: '16px',
    height: '16px',
    backgroundColor: '#fff',
    border: '2px solid #0047AB',
    borderRadius: '50%',
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
      @keyframes fadeInUp {
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `;
    document.head.appendChild(style);

    // Add resize listener
    const handleResize = () => {
      window.location.reload(); // Force refresh on resize to update styles
    };

    window.addEventListener('resize', handleResize);
    return () => {
      document.head.removeChild(style);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div style={containerStyle}>
      <div style={overlayStyle}>
        <div style={splitLayoutStyle}>
          {/* Left Side Content */}
          <div style={leftSideStyle}>
            <h1 style={leftTitleStyle}>
              EMPOWERING YOUR BUSINESS WITH 
              TELECOM & IT SOLUTIONS
            </h1>
          </div>

          {/* Right Side Timeline */}
          <div style={rightSideStyle}>
            <div style={timelineStyle}>
              <div style={sectionStyle(0.2)}>
                <div style={circleStyle}></div>
                <h2 style={headingStyle}>TELECOM & IOT SERVICES</h2>
                <p style={paragraphStyle}>
                  Your business stays connected with fast internet, custom IOT plans, and tailored voice solutions.
                </p>
              </div>

              <div style={sectionStyle(0.4)}>
                <div style={circleStyle}></div>
                <h2 style={headingStyle}>MANAGED SERVICES</h2>
                <p style={paragraphStyle}>
                  Get comprehensive IT and security, manage warehouses, and access skilled manpower support.
                </p>
              </div>

              <div style={sectionStyle(0.6)}>
                <div style={circleStyle}></div>
                <h2 style={headingStyle}>AI & DATA WAREHOUSE</h2>
                <p style={paragraphStyle}>
                  Receive consulting for SAP, Oracle, and Microsoft, plus complete data warehousing guidance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


  const ConsultingSlide = memo(() => {
    const processCardsRef = useRef([]);
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  
    useEffect(() => {
      const handleResize = () => {
        setWindowWidth(window.innerWidth);
      };
  
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);
  
    useEffect(() => {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }
        });
      }, {
        threshold: 0.1
      });
  
      processCardsRef.current.forEach((card) => {
        if (card) {
          card.style.opacity = '0';
          card.style.transform = 'translateY(50px)';
          observer.observe(card);
        }
      });
  
      return () => {
        processCardsRef.current.forEach((card) => {
          if (card) observer.unobserve(card);
        });
      };
    }, []);
  
    const getResponsiveStyles = () => {
      const isMobile = windowWidth <= 768;
      const isTablet = windowWidth <= 1024 && windowWidth > 768;
  
      return {
        container: {
          position: 'relative',
          width: '100%',
          minHeight: isMobile ? '800px' : '120vh',
          backgroundImage: 'url("/abt4.png")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          display: 'flex',
          alignItems: 'center',
          padding: isMobile ? '40px 20px' : '20px',
        },
  
        mainWrapper: {
          width: '100%',
          maxWidth: '1400px',
          margin: '0 auto',
        },
  
        contentWrapper: {
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'center' : 'flex-start',
          gap: isMobile ? '40px' : '32px',
          padding: isMobile ? '20px 0' : '0',
        },
  
        leftSection: {
          flex: isMobile ? '1' : '0 0 41.666667%',
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? '30px' : '20px',
          alignItems: isMobile ? 'center' : 'flex-start',
          width: '100%',
          textAlign: isMobile ? 'center' : 'left',
        },
  
        rightSection: {
          flex: '0 0 42.666667%',
          position: 'relative',
          display: isMobile ? 'flex' : 'flex',
          justifyContent: 'center',
          width: isMobile ? '100%' : 'auto',
        },
  
        title: {
          fontSize: isMobile ? '38px' : isTablet ? '44px' : '54px',
          fontWeight: '600',
          color: 'white',
          marginLeft: isMobile ? '0' : '70px',
          fontFamily: 'Oswald, sans-serif',
          lineHeight: isMobile ? '48px' : isTablet ? '60px' : '80px',
          textAlign: isMobile ? 'center' : 'left',
        },
  
        timeline: {
          position: 'absolute',
          left: isMobile ? '20px' : '50%',
          top: '0',
          bottom: '0',
          width: '2px',
          background: 'linear-gradient(to bottom, #0047AB 33%, transparent 33%)',
          backgroundSize: '8px 24px',
          display: isMobile ? 'block' : 'block',
        },
  
        processCardsContainer: {
          display: 'flex',
          flexDirection: 'column',
          gap: '30px',
          marginRight: isMobile ? '0' : '120px',
          marginLeft: isMobile ? '30px' : '0',
          marginTop: isMobile ? '20px' : '-100px',
          position: 'relative',
          maxWidth: isMobile ? '100%' : '600px',
          width: '100%',
        },
  
        processCard: {
          position: 'relative',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(8px)',
          borderRadius: '8px',
          padding: '20px',
          width: isMobile ? 'calc(100% - 40px)' : '410px',
          marginLeft: '20px',
          transition: 'all 0.5s ease',
        },
  
        button: {
          backgroundColor: 'transparent',
          color: 'white',
          padding: '12px 24px',
          fontFamily: 'Oswald, sans-serif',
          fontSize: isMobile ? '14px' : '16px',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          textDecoration: 'none',
          border: '2px solid white',
          marginLeft: isMobile ? '-20px' : '70px',
          marginTop: isMobile ? '10px' : '0',
          ':hover': {
            backgroundColor: 'white',
            color: 'black',
          },
        },
  
        stepTitle: {
          fontSize: isMobile ? '20px' : '24px',
          color: 'white',
          lineHeight: isMobile ? '32px' : '38px',
          fontWeight: '700',
          marginBottom: '8px',
        },
  
        stepDescription: {
          fontSize: isMobile ? '13px' : '14px',
          color: 'white',
          fontWeight: '200',
          fontFamily: 'Inter, sans-serif',
          lineHeight: isMobile ? '20px' : '22px',
        },
      };
    };
  
    const styles = getResponsiveStyles();
  
    const processCards = [
      {
        title: 'DISCOVERY AND ANALYTICS',
        description: "Perform a analysis of the client's existing IT systems."
      },
      {
        title: 'TAILORED SOLUTIONS',
        description: 'Develop IT solutions based on the analysis phase.'
      },
      {
        title: 'DEPLOYMENT AND SUPPORT',
        description: 'Regularly communicate with our client to stay connect.'
      }
    ];
  
    return (
      <div style={styles.container}>
        <div style={styles.mainWrapper}>
          <div style={styles.contentWrapper}>
            <div style={styles.leftSection}>
              <h1 style={styles.title}>
                BEST PATHWAY TO<br/> OUR CLIENTS
              </h1>
              <button 
                style={styles.button} 
                onClick={() => console.log('Contact button clicked')}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.color = 'black';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = 'white';
                }}
              >
                {`CONTACT US >>`}
              </button>
            </div>
  
            <div style={styles.rightSection}>
              <div style={styles.timeline}></div>
              <div style={styles.processCardsContainer}>
                {processCards.map((card, i) => (
                  <div
                    key={card.title}
                    ref={(el) => (processCardsRef.current[i] = el)}
                    style={{
                      ...styles.processCard,
                      opacity: 0,
                      transform: 'translateY(50px)',
                    }}
                  >
                    <div style={styles.stepTitle}>{card.title}</div>
                    <div style={styles.stepDescription}>{card.description}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  });
  
  
const RegularSlide = memo(({ slide, index, isMobile, isAnimating }) => (
  <>
    <img
      src={slide.image}
      alt={`Slide ${index + 1}`}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        position: 'absolute',
        top: 0,
        left: 0
      }}
    />
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.3)',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      padding: isMobile ? '20px' : '0 5%',
      overflow: 'auto'
    }}>
      <div style={{
        flex: isMobile ? '1' : '0 0 60%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '20px',
        alignItems: isMobile ? 'center' : 'flex-start'
      }}>
        <div style={{
          color: 'white',
          fontSize: 'clamp(32px, 6vw, 54px)',
          fontWeight: '600',
          lineHeight: '1.2',
          marginBottom: '15px',
          fontFamily: 'Oswald, sans-serif',
          textAlign: isMobile ? 'center' : 'left'
        }}>
          {slide.heading.map((line, i) => (
            <React.Fragment key={i}>
              <WordAnimation delay={i * 0.2} isAnimating={isAnimating}>
                {line}
              </WordAnimation>
              <br />
            </React.Fragment>
          ))}
        </div>
        <div style={{
          display: 'flex',
          gap: '20px',
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: isMobile ? 'center' : 'flex-start',
          width: '100%'
        }}>
          {slide.buttons?.map((button, i) => (
            <a
              key={i}
              href="#"
              style={{
                backgroundColor: 'transparent',
                color: 'white',
                padding: '10px 20px',
                fontFamily: 'Oswald, sans-serif',
                fontSize: 'clamp(14px, 3vw, 16px)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textDecoration: 'none',
                border: '2px solid white',
                textAlign: 'center',
                minWidth: isMobile ? '200px' : 'auto'
              }}
            >
              {button}
            </a>
          ))}
        </div>
      </div>
      <Stats
        isMobile={isMobile}
        currentSlide={index}
        isAnimating={isAnimating}
        counts={useCounterAnimation()}
      />
    </div>
  </>
));

const TeltonikaCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth <= 768;

  const slides = [
    {
      image: '/bg_1.png',
      heading: ['HARNESS', 'THE WIRELESS', 'POWER OF 5G'],
      buttons: ['PRODUCT PORTFOLIO »', 'REACH OUT TO US »']
    },
    {
      type: 'repair',
      image: '/ai_bg_3.png',
    },
    {
      type: 'consulting',
    }
  ];

  useEffect(() => {
    let slideInterval;
    if (!isPaused) {
      slideInterval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 4000);
    }
    return () => clearInterval(slideInterval);
  }, [isPaused, slides.length]);

  useEffect(() => {
    setIsAnimating(false);
    const timer = setTimeout(() => setIsAnimating(true), 50);
    return () => clearTimeout(timer);
  }, [currentSlide]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  return (
    <div
      style={{
        width: '100%',
        height: '100vh',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: 'Oswald, sans-serif'
      }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {!isMobile && (
        <>
          <button
            style={{
              position: 'absolute',
              top: '50%',
              left: '20px',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              padding: '12px 16px',
              cursor: 'pointer',
              zIndex: 10,
              border: 'none',
              borderRadius: '4px'
            }}
            onClick={prevSlide}
          >
            <ChevronLeft size={28} />
          </button>
          <button
            style={{
              position: 'absolute',
              top: '50%',
              right: '20px',
              transform: 'translateY(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              padding: '12px 16px',
              cursor: 'pointer',
              zIndex: 10,
              border: 'none',
              borderRadius: '4px'
            }}
            onClick={nextSlide}
          >
            <ChevronRight size={28} />
          </button>
        </>
      )}
      
      {slides[currentSlide].type === 'repair' ? (
        <RepairSlide />
      ) : slides[currentSlide].type === 'consulting' ? (
        <ConsultingSlide />
      ) : (
        <RegularSlide
          slide={slides[currentSlide]}
          index={currentSlide}
          isMobile={isMobile}
          isAnimating={isAnimating}
        />
      )}
    </div>
  );
};

export default TeltonikaCarousel;